import { addToCart } from '../utils/addToCart';
import ButtonModular from './button-modular';

type GiftCardVariant = {
  id: number;
  available: boolean;
};

export default class GiftCardSelector extends HTMLElement {
  private submitButton: ButtonModular | null = null;
  private form: HTMLFormElement | null = null;
  private variants: GiftCardVariant[] = [];
  private currentVariant: GiftCardVariant | null = null;

  private onFormChangeBound = this.onFormChange.bind(this);
  private onFormSubmitBound = this.onFormSubmit.bind(this);

  connectedCallback(): void {
    this.form = this.querySelector('[data-gift-card-selector-form-ref="form"]');
    this.submitButton = this.querySelector('[data-gift-card-selector-form-ref="submit-button"]');

    if (this.dataset.variants) {
      try {
        this.variants = JSON.parse(this.dataset.variants) as GiftCardVariant[];
      } catch {
        this.variants = [];
      }
    }

    this.addEventListeners();

    customElements.whenDefined('button-modular').then(() => {
      this.submitButton = this.querySelector('[data-gift-card-selector-form-ref="submit-button"]');
      this.syncSelection();
    });
  }

  addEventListeners(): void {
    if (this.form) {
      this.form.addEventListener('change', this.onFormChangeBound);
      this.form.addEventListener('submit', this.onFormSubmitBound);
    }
  }

  removeEventListeners(): void {
    if (this.form) {
      this.form.removeEventListener('change', this.onFormChangeBound);
      this.form.removeEventListener('submit', this.onFormSubmitBound);
    }
  }

  private findVariantById(id: string): GiftCardVariant | undefined {
    return this.variants.find((variant) => variant.id.toString() === id);
  }

  private getSelectedVariantId(): string | null {
    if (!this.form) {
      return null;
    }

    return this.form.querySelector<HTMLInputElement>('input[name="variant"]:checked')?.value ?? null;
  }

  private updateURL(variantId: string): void {
    const url = new URL(window.location.href);
    url.searchParams.set('variant', variantId);
    window.history.replaceState({}, '', url.toString());
  }

  private syncSelection(updateUrl = false): void {
    const variantId = this.getSelectedVariantId();

    if (!variantId) {
      this.currentVariant = null;

      if (this.submitButton) {
        this.submitButton.gotToStateIndex(0);
      }

      return;
    }

    const variant = this.findVariantById(variantId);

    if (variant?.available) {
      this.currentVariant = variant;

      if (this.submitButton) {
        this.submitButton.gotToStateIndex(1);
      }

      if (updateUrl) {
        this.updateURL(variantId);
      }
    } else {
      this.currentVariant = variant ?? null;

      if (this.submitButton) {
        this.submitButton.gotToStateIndex(0);
      }
    }
  }

  onFormChange(): void {
    this.syncSelection(true);
  }

  onFormSubmit(event: Event): void {
    event.preventDefault();

    if (this.currentVariant !== null && this.currentVariant.available) {
      addToCart({
        id: this.currentVariant.id.toString(),
        quantity: 1,
        gift_card: true,
      });
    }
  }

  disconnectedCallback(): void {
    this.removeEventListeners();
  }
}

customElements.define('gift-card-selector', GiftCardSelector);
